import type { Task, TaskStatus } from '../services/taskService';

export const isPlanningTask = (task: Task): boolean => task.task_type === 'planning';
export const isRunningPlanningTask = (task: Task): boolean => (
  isPlanningTask(task) && ['queued', 'ai_in_progress'].includes(task.status)
);

/** The short state word the planning checklists show beside a task. */
export const planningTaskStateLabel = (status?: TaskStatus): string => {
  if (!status) return 'Ready';
  const labels: Partial<Record<TaskStatus, string>> = {
    backlog: 'Waiting', todo: 'Ready', queued: 'Queued', ai_in_progress: 'Running',
    human_in_progress: 'Running', error: 'Retry', done: 'Done', paused: 'Paused',
  };

  return labels[status] ?? status.charAt(0).toUpperCase() + status.slice(1);
};

const designScope = (task: Task): string | undefined => {
  const metadata = task.planning_metadata;

  if (!isPlanningTask(task) || !metadata?.run_id
    || !['design_first_page', 'design_remaining'].includes(metadata.stage ?? '')) return undefined;

  return JSON.stringify([task.project_id, metadata.run_id, metadata.stage]);
};

/** Keep orchestration stages in the source data, but show named pages only once. */
export const visiblePlanningTasks = (tasks: Task[]): Task[] => {
  const pageScopes = new Set(tasks.filter((task) => task.planning_metadata?.artifact_type === 'design_page')
    .map(designScope).filter((scope): scope is string => Boolean(scope)));

  return tasks.filter((task) => task.planning_metadata?.artifact_type !== 'stage'
    || !pageScopes.has(designScope(task) ?? '')).map((task) => {
    const metadata = task.planning_metadata;
    const name = metadata?.artifact_name;

    // Old cached/SSE records use this generated prefix; preserve custom titles.
    if (isPlanningTask(task) && metadata?.artifact_type === 'design_page' && name
      && task.title === `Design ${name}`.slice(0, 255)) return { ...task, title: name.slice(0, 255) };

    return task;
  });
};

/** A stage may still be finalizing or fail after every named page is done. */
export const unrepresentedPlanningStages = (tasks: Task[]): Task[] => {
  const visibleIds = new Set(visiblePlanningTasks(tasks).map((task) => task.id));
  const active = new Set(['queued', 'ai_in_progress', 'human_in_progress']);
  const state = (task: Task) => active.has(task.status) ? 'running' : task.status;

  return tasks.filter((task) => !visibleIds.has(task.id)
    && ['running', 'error', 'cancelled', 'paused'].includes(state(task))
    && !tasks.some((child) => child.planning_metadata?.artifact_type !== 'stage'
      && designScope(child) === designScope(task) && state(child) === state(task)));
};

const timeInMillis = (value: string): number => (
  Date.parse(/[Zz]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)
);

/** Shared sections reuse their owner's measured generation, without another execution. */
export const sharedPlanningTaskSeconds = (task: Task): number | null => {
  const seconds = task.planning_metadata?.shared_generation_seconds;

  return task.status === 'done' && !task.started_at
    && !(Number(task.planning_metadata?.accumulated_active_seconds) > 0)
    && typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0
    ? seconds : null;
};

const formatDuration = (seconds: number): string => (
  `${Math.floor(seconds / 60)}m ${Math.floor(seconds) % 60}s`
);

// eslint-disable-next-line max-statements
export const planningTaskDuration = (task: Task, now: number): string => {
  const accumulated = Math.max(0, Number(task.planning_metadata?.accumulated_active_seconds) || 0);
  const sharedSeconds = sharedPlanningTaskSeconds(task);

  if (sharedSeconds !== null) return formatDuration(sharedSeconds);
  if (!task.started_at && !accumulated) return '';
  const pausedAt = task.status === 'paused'
    ? task.planning_metadata?.paused_at || task.updated_at
    : null;
  const end = task.completed_at ? timeInMillis(task.completed_at)
    : typeof pausedAt === 'string' ? timeInMillis(pausedAt) : now;
  const pausedSeconds = Number(task.planning_metadata?.paused_duration_seconds) || 0;
  const current = task.started_at && !['queued', 'backlog', 'todo'].includes(task.status)
    ? Math.max(0, (end - timeInMillis(task.started_at)) / 1000 - pausedSeconds) : 0;
  const seconds = Math.floor(accumulated + current);

  if (!Number.isFinite(seconds)) return '';

  return formatDuration(seconds);
};

const STAGE_ORDER = ['srd', 'user_flows', 'design_first_page', 'design_remaining', 'architecture', 'pm'];
const stageOrder = (task: Task): number => {
  const index = STAGE_ORDER.indexOf(task.planning_metadata?.stage ?? '');

  return index < 0 ? STAGE_ORDER.length : index;
};

const displayOrder = (task: Task): number => {
  const value = task.planning_metadata?.display_order;

  return typeof value === 'number' && Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};
const creationOrder = (left: Task, right: Task): number => (
  (left.task_number ?? left.position) - (right.task_number ?? right.position)
);

/** Keep a flat list in planned page/section order as parallel tasks finish. */
// eslint-disable-next-line max-statements
/** A review finding still being fixed (13 Oct 2026: they were listed past every section row, out of sight). */
const isOpenReviewFinding = (task: Task): boolean => task.planning_metadata?.artifact_type === 'review_issue'
  && task.status !== 'done';

export const orderedPlanningTasks = (tasks: Task[]): Task[] => {
  const ordered = [...tasks].sort((left, right) => stageOrder(left) - stageOrder(right)
    || creationOrder(left, right));
  const known = new Map(ordered.flatMap((task) => [task.id, task.planning_key, task.planning_metadata?.task_key]
    .filter((key): key is string => Boolean(key)).map((key) => [key, task] as const)));
  const children = new Map<string, Task[]>();
  const visited = new Set<string>();
  const result: Task[] = [];

  ordered.forEach((task) => {
    const parent = known.get(task.planning_metadata?.parent_key ?? '');

    if (parent && parent.id !== task.id) children.set(parent.id, [...(children.get(parent.id) ?? []), task]);
  });
  const visit = (task: Task) => {
    if (visited.has(task.id)) return;
    visited.add(task.id);
    result.push(task);
    const descendants = children.get(task.id) ?? [];
    const retries = descendants.filter((child) => child.planning_metadata?.artifact_type === 'retry_attempt');
    const findings = descendants.filter(isOpenReviewFinding);

    // Put the newest retry beside its stage so it is visible as soon as clicked, then the review findings being
    // fixed now: each is a task with the screen or code it was found on.
    retries.reverse().forEach(visit);
    findings.sort(creationOrder).forEach(visit);
    descendants.filter((child) => child.planning_metadata?.artifact_type !== 'retry_attempt'
      && !isOpenReviewFinding(child))
      .sort((left, right) => displayOrder(left) - displayOrder(right) || creationOrder(left, right))
      .forEach(visit);
  };

  ordered.filter((task) => !known.has(task.planning_metadata?.parent_key ?? '')).forEach((task) => visit(task));
  ordered.forEach((task) => visit(task));

  return result;
};
